import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import API from "@/lib/api";
import { BLINDSPOT_FALLBACK } from "@/data/leaderboard";

const ORG_COLORS = {
  Anthropic: "#e8a87c",
  Google:    "#4285f4",
  OpenAI:    "#74aa9c",
};

function normalize(row) {
  return { ...row, noImage: row.noImage ?? row.no_image ?? null };
}

function OrgDot({ org }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: ORG_COLORS[org] || "#888", flexShrink: 0, marginRight: 6,
    }} />
  );
}

export default function BlindspotDetailPage() {
  const [results, setResults] = useState(BLINDSPOT_FALLBACK.map(normalize));

  useEffect(() => {
    axios.get(`${API}/hf/blindspot-leaderboard`).then(r => {
      if (r.data?.rows?.length) setResults(r.data.rows.map(normalize));
    }).catch(() => {});
  }, []);

  const BY_TOLD     = [...results].sort((a, b) => b.told - a.told);
  const BY_GROUNDED = [...results].filter(r => r.grounded !== null).sort((a, b) => b.grounded - a.grounded);
  const maxTold     = Math.max(...results.map(r => r.told));
  const maxGrounded = Math.max(...BY_GROUNDED.map(r => r.grounded));

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>
      <Helmet>
        <title>BLINDSPOT — Diagram Labeling with No-Image Control · Nalandadata.ai</title>
        <meta name="description" content="BLINDSPOT: 9 frontier models, 181 unlabelled science diagrams, 4 conditions. 8 of 9 models keep 70–100% of their score when the image is removed." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nalandadata.ai/benchmarks/blindspot" />
        <meta property="og:title" content="BLINDSPOT — Diagram Labeling with No-Image Control · Nalandadata.ai" />
        <meta property="og:description" content="BLINDSPOT: 9 frontier models, 181 unlabelled science diagrams, 4 conditions. 8 of 9 models keep 70–100% of their score when the image is removed." />
        <meta property="og:image" content="https://nalandadata.ai/og-social.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BLINDSPOT — Diagram Labeling with No-Image Control · Nalandadata.ai" />
        <meta name="twitter:description" content="BLINDSPOT: 9 frontier models, 181 unlabelled science diagrams, 4 conditions. 8 of 9 models keep 70–100% of their score when the image is removed." />
        <meta name="twitter:image" content="https://nalandadata.ai/og-social.png" />
      </Helmet>

      {/* Breadcrumb */}
      <div className="s-breadbar">
        <div className="s-wrap s-breadbar-inner">
          <Link to="/">Home</Link>
          <span style={{ opacity: .4 }}>/</span>
          <Link to="/benchmarks">Benchmarks</Link>
          <span style={{ opacity: .4 }}>/</span>
          <span>BLINDSPOT</span>
        </div>
      </div>

      {/* Page header */}
      <div className="s-detail-header">
        <div className="s-wrap">
          <p className="s-eyebrow">Diagram Labeling · Name-Set F1</p>
          <h1>Are models reading the diagram, or <span className="hl">reciting from memory?</span></h1>
          <p className="sub">A model is shown an unlabelled scientific diagram and must name every part a leader line points to. The headline result is not the leaderboard — it is the <b>no-image control</b>: the same prompt, picture removed. 8 of 9 models keep 70–100% of their score.</p>
        </div>
      </div>

      {/* Stat strip */}
      <div className="s-stat-strip">
        <div className="s-stat-item">
          <div className="s-stat-num">8 <span className="s-stat-unit">of 9</span></div>
          <div className="s-stat-lbl">Models that kept 70–100% of their score when the image was removed.</div>
        </div>
        <div className="s-stat-item">
          <div className="s-stat-num">66<span className="s-stat-unit">%</span></div>
          <div className="s-stat-lbl">Items answerable from memory alone — at least one model scored F1 ≥ 0.5 with no image.</div>
        </div>
        <div className="s-stat-item">
          <div className="s-stat-num">181</div>
          <div className="s-stat-lbl">Benchmark items across biology diagrams, each requiring a complete list of labeled parts.</div>
        </div>
        <div className="s-stat-item">
          <div className="s-stat-num">4</div>
          <div className="s-stat-lbl">Conditions per model: blind, told, desc, no-image — isolating recognition, naming, and prior knowledge.</div>
        </div>
      </div>

      {/* Key finding callout */}
      <section className="s-band" style={{ padding: "48px 0" }}>
        <div className="s-wrap">
          <div style={{
            background: "linear-gradient(135deg, rgba(255,200,100,0.06) 0%, rgba(255,150,50,0.04) 100%)",
            border: "1px solid rgba(255,200,100,0.18)",
            borderRadius: 12, padding: "32px 36px",
          }}>
            <p className="s-eyebrow" style={{ color: "#f5a623", marginBottom: 10 }}>Key finding</p>
            <h2 style={{ fontSize: "clamp(20px,2.8vw,32px)", fontWeight: 800, lineHeight: 1.1, margin: "0 0 12px" }}>
              Ranking by what the image actually contributes reverses the leaderboard.
            </h2>
            <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.65, maxWidth: "70ch", margin: 0 }}>
              The top model by <em>told</em> score (Gemini 3.1 Pro, 60.2%) keeps 71% of its score with no image.
              {" "}The model with the highest <em>grounded contribution</em> — the score attributable to actually seeing the diagram — is GPT-5.6-luna (+46.2 pts), which sits fifth by told score.
              {" "}GPT-4o-mini is last by told score and contributes essentially nothing from the image (+0.2 pts, CI contains zero).
            </p>
          </div>
        </div>
      </section>

      {/* Main results table */}
      <section className="s-band alt" id="results">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Results</p>
            <h2>All 9 models · 4 conditions</h2>
            <p className="lead">Sorted by <em>told</em> score (image + subject name). The <b>grounded contribution</b> column (told − no-image) measures how much the image actually contributed.</p>
          </div>

          <div style={{ overflowX: "auto", marginTop: 24 }}>
            <table style={{
              width: "100%", borderCollapse: "collapse", fontSize: 13,
              fontFamily: "var(--mono)", minWidth: 680,
            }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--line)" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "var(--muted)", fontWeight: 600, whiteSpace: "nowrap" }}>Model</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>Blind</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>Told</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>Desc</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>No-image</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#f5a623", fontWeight: 700 }}>Grounded</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "var(--muted)", fontWeight: 600 }}>Recited</th>
                </tr>
              </thead>
              <tbody>
                {BY_TOLD.map((r, i) => (
                  <tr key={r.model} style={{
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                    background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                  }}>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 0 }}>
                        <OrgDot org={r.org} />
                        <span style={{ color: "var(--text)" }}>{r.model}</span>
                      </span>
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--muted)" }}>{r.blind}%</td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--text)", fontWeight: 600 }}>{r.told}%</td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--muted)" }}>{r.desc}%</td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--muted)" }}>
                      {r.noImage !== null ? `${r.noImage}%` : <span style={{ opacity: 0.35 }}>—</span>}
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: r.grounded !== null ? "#f5a623" : "var(--muted)", fontWeight: r.grounded !== null ? 700 : 400 }}>
                      {r.grounded !== null ? `+${r.grounded}` : <span style={{ opacity: 0.35 }}>—</span>}
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 12px", color: "var(--muted)" }}>
                      {r.recited !== null ? `${r.recited}%` : <span style={{ opacity: 0.35 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "var(--muted-2)", marginTop: 12 }}>
            <b>Grounded contribution</b> = told − no-image (pp). <b>Recited</b> = no-image ÷ told (%).
            claude-sonnet-4 has no no-image row — model retired from Anthropic's API.
            Top two models are a statistical tie (bootstrap ±4 pts).
          </p>
        </div>
      </section>

      {/* Grounded contribution bar chart */}
      <section className="s-band">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Reversal view</p>
            <h2>Sorted by grounded contribution.</h2>
            <p className="lead">How much score came from actually seeing the diagram. GPT-5.6-luna leads; GPT-4o-mini's image contributes statistically nothing.</p>
          </div>
          <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 10 }}>
            {BY_GROUNDED.map(r => (
              <div key={r.model} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 190, flexShrink: 0, display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--mono)", fontSize: 12, color: "var(--muted)" }}>
                  <OrgDot org={r.org} />
                  {r.model}
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,0.06)", borderRadius: 4, height: 18, overflow: "hidden" }}>
                  <div style={{
                    width: `${((r.grounded / maxGrounded) * 100).toFixed(1)}%`,
                    height: "100%", background: r.grounded < 5 ? "rgba(255,100,100,0.5)" : "rgba(245,166,35,0.65)",
                    borderRadius: 4, transition: "width 0.4s",
                  }} />
                </div>
                <div style={{ width: 56, textAlign: "right", fontFamily: "var(--mono)", fontSize: 13, color: "#f5a623", fontWeight: 700 }}>+{r.grounded}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Findings summary */}
      <section className="s-band alt">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Findings</p>
            <h2>Nine findings from the benchmark.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 16, marginTop: 24 }}>
            {[
              { n: "F1", title: "Recognition is the bottleneck", body: "Every model gains from being told the subject. The naming ability is there; the recognition is not." },
              { n: "F2", title: "Verbose context adds nothing", body: "9 models, every desc vs told difference within ±1.6 pts. Describing the diagram in prose does not help." },
              { n: "F3", title: "Recognition gap is closing, unevenly", body: "blind→told gap: Anthropic −8.0 pts, Google −5.0, OpenAI −2.9 across model generations." },
              { n: "F4", title: "8 of 9 models score high with no image", body: "Recitation is older, not newer — 2.5-era models recite 80–100%, current ones ~70%." },
              { n: "F5", title: "66% of items answerable from memory", body: "120 of 181 items: the subject is famous enough that naming it is most of the answer." },
              { n: "F6", title: "Bluffing and abstaining land at the same F1", body: "GPT-5.6-luna declines 81% of items with no image; Claude Opus 5 never declines. Same F1 either way." },
              { n: "F7", title: "Agreement ≠ correctness", body: "31% of consensus names are absent from the answer key. Models agree on wrong anatomy." },
              { n: "F8", title: "Selection bias tested — finding holds", body: "Two control sets (pool + baseline) confirm 69–73% of the effect survives in unbiased samples." },
              { n: "F9", title: "Statistically solid reversal", body: "+28.6 pts difference, 95% CI +23.2 to +33.6. The ranking flip is not noise." },
            ].map(f => (
              <div key={f.n} style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px 22px" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#f5a623", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>{f.n}</div>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, lineHeight: 1.6 }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Caveats */}
      <section className="s-band">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Caveats</p>
            <h2>What this benchmark can and cannot claim.</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20, maxWidth: "72ch" }}>
            {[
              { id: "7.1", text: "Failure-only set — items were kept because baseline models failed them. Tested with two controls; the finding holds." },
              { id: "7.2", text: "Baseline models ran via OpenRouter, controls via native APIs. Mixed routes for the four baseline models' grounded contribution." },
              { id: "7.3", text: "8 items reproduce the figure at small scale. Excluding them changes nothing." },
              { id: "7.5", text: "Item-sampling variance measured by bootstrap: ±4 pts. The top two models are a statistical tie." },
              { id: "7.6", text: "Biology only. Untested on other domains." },
              { id: "7.7", text: "Contamination not separated from general knowledge — these are published textbook figures." },
            ].map(c => (
              <div key={c.id} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "#f5a623", fontWeight: 700, flexShrink: 0, paddingTop: 2 }}>{c.id}</span>
                <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", lineHeight: 1.65 }}>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="s-band alt">
        <div className="s-wrap">
          <div className="s-sec-head">
            <p className="s-eyebrow">Methodology</p>
            <h2>How the benchmark was built.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 16, marginTop: 24 }}>
            {[
              { step: "01", title: "Item selection", body: "181 biology diagrams from three sources. Items kept only if every baseline model failed them." },
              { step: "02", title: "Anonymisation", body: "All items assigned NALANDA-N public codes (salted SHA-256 order). Internal IDs and source book names not released." },
              { step: "03", title: "Four conditions", body: "Each model scored on blind (image only), told (image + subject), desc (image + description), and no-image (subject, no picture)." },
              { step: "04", title: "Synonym-aware F1", body: "Scoring handles Latin plurals, domain synonyms (mitral = bicuspid = left AV valve), and morphological variants with tight fuzzy guards." },
            ].map(s => (
              <div key={s.step} style={{ background: "var(--ink)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px 22px" }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted-2)", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 6 }}>{s.step}</div>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: "var(--muted)", margin: 0, lineHeight: 1.6 }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Links */}
      <section className="s-band s-cs-cta">
        <div className="s-wrap" style={{ textAlign: "center" }}>
          <p className="s-eyebrow" style={{ justifyContent: "center" }}>Reproducibility</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.08, margin: "0 0 8px" }}>Dataset and code are public.</h2>
          <p style={{ color: "var(--muted)", maxWidth: "52ch", margin: "0 auto 28px", fontSize: 15 }}>
            Source images are not distributable (copyrighted textbooks and a proprietary question bank). Scores, predicted names, answer keys, and all analysis scripts are open.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a className="s-btn primary" href="https://huggingface.co/datasets/Nalandadata/BLINDSPOT" target="_blank" rel="noopener noreferrer">Full dataset (gated) →</a>
            <a className="s-btn ghost" href="https://huggingface.co/datasets/Nalandadata/BLINDSPOT-sample" target="_blank" rel="noopener noreferrer">10-row sample →</a>
            <Link className="s-btn ghost" to="/contact">Submit a model →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
